import React from "react";
import { ExerciseEditorProps } from "./exercise-editor-types";
import { ScrambleExerciseContent } from "@org/ui";
import { Button } from "@org/ui";
import { Label } from "@org/ui";
import { Input } from "@org/ui";
import { TextArea } from "@org/ui";
import { XIcon, GripVertical, Shuffle } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const createSentenceId = () => `scramble_sentence_${Math.random().toString(36).slice(2, 10)}`;

interface SortableSentenceItemProps {
  sentence: ScrambleExerciseContent["data"]["sentences"][number];
  sentenceIndex: number;
  contentId: string;
  sentencesLength: number;
  onSentenceChange: (index: number, value: string) => void;
  onCorrectionChange: (index: number, value: string) => void;
  onRemove: (index: number) => void;
}

/**
 * Shuffle words for preview
 */
function shuffleWords(sentence: string): string {
  const words = sentence.trim().split(/\s+/).filter(w => w.length > 0);
  if (words.length <= 1) return sentence;
  
  // Simple shuffle
  for (let i = words.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [words[i], words[j]] = [words[j], words[i]];
  }
  return words.join(' ');
}

function SortableSentenceItem({
  sentence,
  sentenceIndex,
  contentId,
  sentencesLength,
  onSentenceChange,
  onCorrectionChange,
  onRemove,
}: SortableSentenceItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sentence.sentenceId ?? `sentence-${sentenceIndex}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const [previewScrambled, setPreviewScrambled] = React.useState<string | null>(null);

  const handlePreviewScramble = () => {
    if (sentence.correctSentence) {
      setPreviewScrambled(shuffleWords(sentence.correctSentence));
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="rounded-lg border p-4 space-y-3 bg-background"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="cursor-grab text-muted-foreground hover:text-foreground touch-none"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-5 w-5" />
          </button>
          <Label>Sentence {sentenceIndex + 1}</Label>
        </div>
        <div className="flex items-center gap-2">
          {sentence.correctSentence && (
            <Button
              variant="ghost"
              size="sm"
              className="text-slate-500 hover:text-slate-700"
              onClick={handlePreviewScramble}
              title="Preview scrambled"
            >
              <Shuffle className="h-4 w-4" />
            </Button>
          )}
          {sentencesLength > 1 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-slate-500 hover:text-red-500"
              onClick={() => onRemove(sentenceIndex)}
            >
              <XIcon className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <Label htmlFor={`sentence-${contentId}-${sentenceIndex}`}>
            Correct Sentence
          </Label>
          <Input
            id={`sentence-${contentId}-${sentenceIndex}`}
            value={sentence.correctSentence}
            onChange={(event) => onSentenceChange(sentenceIndex, event.target.value)}
            placeholder="Enter the correct sentence (words will be scrambled for students)..."
            className="mt-1"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Words will be split by spaces and scrambled for students to rearrange.
          </p>
        </div>

        {previewScrambled && (
          <div className="rounded-md bg-slate-50 border border-slate-200 p-3">
            <p className="text-xs font-medium text-slate-500 mb-1">Preview (scrambled):</p>
            <p className="text-sm text-slate-700">{previewScrambled}</p>
          </div>
        )}

        <div>
          <Label htmlFor={`correction-${contentId}-${sentenceIndex}`}>
            Hint/Correction (optional)
          </Label>
          <TextArea
            id={`correction-${contentId}-${sentenceIndex}`}
            value={sentence.correction || ""}
            onChange={(event) => onCorrectionChange(sentenceIndex, event.target.value)}
            placeholder="Optional hint shown after incorrect submission..."
            className="mt-1"
            rows={2}
          />
        </div>
      </div>
    </div>
  );
}

export const ScrambleExerciseEditor: React.FC<ExerciseEditorProps<ScrambleExerciseContent>> = ({
  content,
  updateContent,
}) => {
  const sentences = content.data.sentences ?? [];

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const applySentences = (nextSentences: ScrambleExerciseContent["data"]["sentences"]) => {
    const normalized = nextSentences.map((sentence, index) => ({
      ...sentence,
      sentenceId: sentence.sentenceId ?? createSentenceId(),
      order: index,
    }));

    updateContent({
      ...content,
      data: {
        ...content.data,
        sentences: normalized,
      },
    });
  };

  const handleSentenceChange = (index: number, value: string) => {
    applySentences(
      sentences.map((sentence, idx) =>
        idx === index ? { ...sentence, correctSentence: value } : sentence
      )
    );
  };

  const handleCorrectionChange = (index: number, value: string) => {
    applySentences(
      sentences.map((sentence, idx) =>
        idx === index ? { ...sentence, correction: value } : sentence
      )
    );
  };

  const handleAddSentence = () => {
    applySentences([
      ...sentences,
      {
        sentenceId: createSentenceId(),
        correctSentence: "",
        order: sentences.length,
        correction: "",
      },
    ]);
  };

  const handleRemoveSentence = (index: number) => {
    if (sentences.length <= 1) return;
    applySentences(sentences.filter((_, idx) => idx !== index));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = sentences.findIndex(
        (sentence) => (sentence.sentenceId ?? `sentence-${sentences.indexOf(sentence)}`) === active.id
      );
      const newIndex = sentences.findIndex(
        (sentence) => (sentence.sentenceId ?? `sentence-${sentences.indexOf(sentence)}`) === over.id
      );

      if (oldIndex !== -1 && newIndex !== -1) {
        applySentences(arrayMove(sentences, oldIndex, newIndex));
      }
    }
  };

  const sentenceIds = sentences.map((sentence, index) => sentence.sentenceId ?? `sentence-${index}`);

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-dashed border-muted-foreground/50 bg-muted/30 p-3">
        <p className="text-sm text-muted-foreground">
          Enter sentences that students will need to unscramble. Each sentence's words
          will be shuffled, and students must tap them in the correct order.
        </p>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={sentenceIds} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {sentences.map((sentence, sentenceIndex) => (
              <SortableSentenceItem
                key={sentence.sentenceId ?? `sentence-${sentenceIndex}`}
                sentence={sentence}
                sentenceIndex={sentenceIndex}
                contentId={content.id}
                sentencesLength={sentences.length}
                onSentenceChange={handleSentenceChange}
                onCorrectionChange={handleCorrectionChange}
                onRemove={handleRemoveSentence}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <Button variant="outline" size="sm" onClick={handleAddSentence}>
        Add Sentence
      </Button>
    </div>
  );
};
